import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Table, Card, Button, Space, Input, Tag } from 'antd';
import { PlusOutlined, SearchOutlined, GlobalOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getRowLinkProps, withRowLink } from '../../utils/rowLink';
import { getRemoteConfigs, getRemoteConfigsCount } from '../../graphql/queries';
import './RemoteConfigs.css';

const RemoteConfigs = () => {
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => {
      setSearchTerm(searchInput.trim());
      setPagination((p) => ({ ...p, current: 1 }));
    }, 250);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const queryVars = useMemo(
    () => ({
      page: pagination.current,
      pageSize: pagination.pageSize,
      name: searchTerm || undefined,
    }),
    [pagination, searchTerm],
  );

  const { loading, data } = useQuery(getRemoteConfigs, {
    variables: queryVars,
    fetchPolicy: 'cache-and-network',
  });

  const { data: countData } = useQuery(getRemoteConfigsCount, {
    variables: { name: searchTerm || undefined },
    fetchPolicy: 'cache-and-network',
  });

  const configs = data?.getRemoteConfigs ?? [];
  const total = countData?.getRemoteConfigsCount ?? 0;

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name) => <strong>{name}</strong>,
    },
    {
      title: 'Locales',
      dataIndex: 'locales',
      key: 'locales',
      render: (locales = []) => (
        <Space size={4} wrap>
          {locales.map((l) => (
            <Tag
              key={l.id}
              color={l.locale === 'en' ? 'blue' : 'default'}
              style={{ fontFamily: 'monospace' }}
            >
              {l.locale}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 200,
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 200,
      render: (date) => new Date(date).toLocaleString(),
    },
  ];

  return (
    <div className="remote-configs-container">
      <Card>
        <div className="page-header">
          <div className="header-content">
            <h1>
              <GlobalOutlined style={{ marginRight: 8 }} />
              Remote Configs
            </h1>
            <p>
              Localized JSON payloads referenced by name from RevenueCat metadata.
              Each config has a default <code>en</code> locale plus any extras.
            </p>
          </div>
          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={() => navigate('/remote-configs/create')}
          >
            Create Remote Config
          </Button>
        </div>

        <div className="filters-section">
          <Input
            placeholder="Search by name..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ width: 280 }}
            prefix={<SearchOutlined />}
            allowClear
          />
        </div>

        <Table
          columns={withRowLink(columns, (record) => `/remote-configs/${record.id}`)}
          dataSource={configs}
          loading={loading}
          rowKey="id"
          pagination={{
            ...pagination,
            total,
            showSizeChanger: true,
            showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} configs`,
          }}
          onChange={(p) =>
            setPagination({ current: p.current, pageSize: p.pageSize })
          }
          onRow={(record) => ({
            ...getRowLinkProps(`/remote-configs/${record.id}`, () => navigate(`/remote-configs/${record.id}`)),
          })}
          locale={{
            emptyText: searchTerm
              ? `No Remote Configs match "${searchTerm}".`
              : 'No Remote Configs yet. Create one to get started.',
          }}
        />
      </Card>
    </div>
  );
};

export default RemoteConfigs;
